// Scope + state copy for the Ask thread UI (investigating-analyst Task 14).
//
// Pure, dependency-free, never throws. The wording mirrors "UI copy for unsupported paths" in
// 20-requirements.md and the notes the proxy already puts on `scope` (askDelivery.js scopeOf);
// when the server sent a `scope.note` that text wins, so the UI never infers USB limits on its
// own. Rows are read only for `captureSource` and `_bodyStripped`.

export const ASK_COPY = {
  usbOnly: 'Body search is not readable on USB capture. This session only has request URLs. Capture over the proxy, or open a saved HAR, to search response bodies.',
  usbMixed: 'Some rows are USB capture (request URLs only), so their response bodies were not searched.',
  capHit: 'Stopped at the 150K token limit for one question. This answer is partial. Ask a narrower follow-up to continue.',
  degraded: 'No model was used. This answer is counted from captured requests only.',
  configuredUnknown: 'Configured ad units are not available on USB capture. Showing requested units from URLs only.',
  noLiveRow: 'This request is no longer in the live buffer.',
  cancelTitle: 'Stops waiting. The server run keeps going and may still bill.',
  cancelHelp: 'Cancel stops waiting only. The server run keeps going and may still bill.',
  captureNote: 'Live capture reads request and response bodies. USB capture has request URLs only.',
  stripped: (n) => `${n} older requests no longer have bodies in the live buffer, so they were not searched.`,
  // Plain text, no control: there is no in-panel way to ask over a saved HAR (the agent/ask route takes no sessionId).
  strippedHint: 'Bodies dropped from the live buffer. Open the saved HAR and ask for a complete answer.',
};

// The first example is the golden question from 20-requirements.md.
export const EXAMPLE_QUESTIONS = [
  'Adh requests are not rendering on any pages — what is going on?',
  'How many Google ad requests are in this session, by ad unit?',
  'Which ad units are configured but never requested?',
];

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const isLogcat = (r) => isObj(r) && r.captureSource === 'logcat';

// scopeSummary(rows, source, serverScope?) -> { label, sourceKind, total, usbRows, usbOnly,
//   mixed, usbNote, strippedCount, strippedNote }
// source: { kind: 'live' } | { kind: 'har', name } | 'live' | 'har'.
export function scopeSummary(rows, source, serverScope) {
  const out = {
    label: 'Live session', sourceKind: 'live', total: 0, usbRows: 0, usbOnly: false, mixed: false,
    usbNote: '', strippedCount: 0, strippedNote: '',
  };
  try {
    const kind = isObj(source) ? source.kind : source;
    if (kind === 'har') {
      const name = isObj(source) && typeof source.name === 'string' && source.name ? source.name : 'unnamed';
      out.sourceKind = 'har';
      out.label = `Saved HAR: ${name}`;
    }
    const list = Array.isArray(rows) ? rows.filter(isObj) : [];
    out.total = list.length;
    out.usbRows = list.filter(isLogcat).length;
    const others = list.filter((r) => !isLogcat(r));
    out.usbOnly = out.usbRows > 0 && others.length === 0;
    out.mixed = out.usbRows > 0 && others.length > 0;
    const sc = isObj(serverScope) ? serverScope : null;
    if (out.usbOnly) out.usbNote = ASK_COPY.usbOnly;
    else if (out.mixed) out.usbNote = ASK_COPY.usbMixed;
    const stripped = others.filter((r) => r._bodyStripped).length;
    const fromServer = sc && Number.isFinite(sc.bodyUnavailable) ? sc.bodyUnavailable : 0;
    out.strippedCount = Math.max(stripped, fromServer);
    if (out.strippedCount > 0 && out.sourceKind === 'live') out.strippedNote = ASK_COPY.stripped(out.strippedCount);
  } catch { /* partial summary is fine */ }
  return out;
}

// stateBanners(turn) -> [{ kind: 'error'|'cap'|'degraded'|'usb'|'stripped', role, text }]
// Order is the order the banners render in. The error banner is role "alert", the rest "status".
// The configured-unknown copy shows only when the turn's trail called slot_inventory.
export function stateBanners(turn) {
  const out = [];
  try {
    if (!isObj(turn)) return out;
    const usage = isObj(turn.usage) ? turn.usage : null;
    if (turn.status === 'error') {
      out.push({ kind: 'error', role: 'alert', text: (typeof turn.error === 'string' && turn.error) || 'The question failed.' });
    }
    if (turn.status === 'capHit' || (usage && usage.capHit === true)) {
      out.push({ kind: 'cap', role: 'status', text: ASK_COPY.capHit });
    }
    if (turn.status === 'degraded' || turn.degraded === true) {
      out.push({ kind: 'degraded', role: 'status', text: ASK_COPY.degraded });
    }
    const sc = isObj(turn.scope) ? turn.scope : null;
    if (sc && sc.usb === true) {
      const note = typeof sc.note === 'string' && sc.note ? sc.note : ASK_COPY.usbOnly;
      out.push({ kind: 'usb', role: 'status', text: note });
      const usedInventory = Array.isArray(turn.trail) && turn.trail.some((e) => isObj(e) && e.tool === 'slot_inventory');
      if (usedInventory) out.push({ kind: 'usb', role: 'status', text: ASK_COPY.configuredUnknown });
    }
    if (sc && Number.isFinite(sc.bodyUnavailable) && sc.bodyUnavailable > 0
      && !(typeof sc.note === 'string' && sc.note.includes('no longer have bodies'))) {
      out.push({ kind: 'stripped', role: 'status', text: ASK_COPY.stripped(sc.bodyUnavailable) });
    }
  } catch { /* partial list */ }
  return out;
}

// A turn from the pre-investigation route has only a markdown body: no evidence, next steps,
// follow-ups or trail. Those render through AnswerMarkdown whole; every other turn renders the
// structured answer (its markdown repeats evidence/next and is not shown twice).
export function isLegacyTurn(turn) {
  if (!isObj(turn)) return false;
  const empty = (v) => !Array.isArray(v) || v.length === 0;
  return typeof turn.markdown === 'string' && turn.markdown !== ''
    && empty(turn.evidence) && empty(turn.next) && empty(turn.followUps) && empty(turn.trail)
    && turn.markdown !== turn.answer;
}

// Composer keyboard: Enter sends, Shift+Enter is a newline, Escape blurs. An IME composition
// Enter never sends.
export function composerKeyAction(e) {
  if (!isObj(e)) return null;
  if (e.key === 'Escape') return 'blur';
  if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) return 'send';
  return null;
}

// 12345 -> "12.3K tokens", 950 -> "950 tokens", 1.2M for millions. Non-numbers -> ''.
export function formatTokens(n) {
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) return '';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M tokens`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K tokens`;
  return `${Math.round(n)} tokens`;
}
